import I18n from 'i18n-js';
import React from 'react';
import { IFollow } from '../follow';
import { get } from 'lodash';
import classNames from 'classnames';
import bind from '../bind';
import { trackEvent, MParticleEventName } from '../elog/mparticle_tracker';
import { RCTooltip } from '@reverbdotcom/cadence/components';

type FollowRowAction = (follow: IFollow) => void;

export interface IFollowRowActions {
  deleteFollow: FollowRowAction;
  addGearAlert: FollowRowAction;
  removeGearAlert: FollowRowAction;
}

interface Props {
  follow?: IFollow;
  actions: IFollowRowActions;
  enableDigestToggle?: boolean;
}

interface State {
  confirmRemove: boolean;
}

export default class FollowRow extends React.Component<Props, State> {
  state = {
    confirmRemove: false,
  };

  @bind
  delete(e) {
    e.preventDefault();

    trackEvent({
      eventName: MParticleEventName.RemovedFollow,
      followId: this.props.follow.id,
    });

    this.props.actions.deleteFollow(this.props.follow);
  }

  @bind
  toggleConfirm(e) {
    e.preventDefault();
    this.setState({ confirmRemove: !this.state.confirmRemove });
  }

  link() {
    if (!this.props.follow) { return '#'; }
    return get(this.props.follow, '_links.search.href', '');
  }

  followHasAlert(props) {
    const hasAlert = get(props, 'follow.alert') || false;
    const hasDigestEnabled = get(props, 'follow.digest_enabled') || false;
    return hasAlert || hasDigestEnabled;
  }

  shouldAddAlert() {
    return !this.followHasAlert(this.props);
  }

  @bind
  toggleGearAlert(e) {
    e.preventDefault();

    if (this.shouldAddAlert()) {
      this.props.actions.addGearAlert(this.props.follow);
      return;
    }

    this.props.actions.removeGearAlert(this.props.follow);
  }

  renderDetails() {
    if (!this.props.follow.subtitle) { return this.props.follow.header; }

    return (
      <a href={this.link()}>
        {this.props.follow.header}
        <small>
          {this.props.follow.subtitle}
        </small>
      </a>
    );
  }

  gearAlertTooltipText() {
    if (this.shouldAddAlert()) {
      return I18n.t('commons.addFollowToFeed.addToGearAlerts');
    }

    return I18n.t('commons.addFollowToFeed.removeFromGearAlerts');
  }

  renderGearAlertButton() {
    if (!get(this.props, 'follow._links.alert') && !this.props.enableDigestToggle) return null;

    const classes = classNames(
      'feed-customize__item__action',
      { 'feed-customize__item__action--add-gear-alert': this.shouldAddAlert() },
      { 'feed-customize__item__action--remove-gear-alert': !this.shouldAddAlert() },
    );

    const text = this.gearAlertTooltipText();

    return (
      <RCTooltip text={text} type="plain">
        <button
          className={classes}
          onClick={this.toggleGearAlert}
          aria-label={text}
          type="button"
        />
      </RCTooltip>
    );
  }

  renderImage() {
    if (!get(this.props, 'follow._links.image.href')) { return; }
    const src = this.props.follow._links.image.href;

    return (
      <a
        className="feed-customize__item__image"
        href={this.link()}
        style={{ backgroundImage: `url('${src}')` }}
      />
    );
  }

  renderActions() {
    return (
      <div className="feed-customize__item__actions">
        <RCTooltip text={I18n.t('commons.addFollowToFeed.followSearch')} type="plain">
          <a
            href={this.link()}
            className="feed-customize__item__action feed-customize__item__action--visit"
            aria-label={I18n.t('commons.addFollowToFeed.followSearch')}
          />
        </RCTooltip>
        {this.renderGearAlertButton()}
        <RCTooltip text={I18n.t('commons.addFollowToFeed.remove.removeFromFeed')} type="plain">
          <button
            onClick={this.toggleConfirm}
            className="feed-customize__item__action feed-customize__item__action--confirm"
            aria-label={I18n.t('commons.addFollowToFeed.remove.removeFromFeed')}
            type="button"
          />
        </RCTooltip>
      </div>
    );
  }

  renderFollow() {
    const classes = classNames(
      'feed-customize__item',
      { 'feed-customize__item--placeholder': !this.props.follow },
    );

    return (
      <div className={classes}>
        <div className="feed-customize__item__main">
          {this.renderImage()}
          <div className="feed-customize__item__details">
            {this.props.follow && this.renderDetails()}
          </div>
        </div>
        {this.props.actions && this.renderActions()}
      </div>
    );
  }

  renderConfirm() {
    return (
      <div className="feed-customize__item feed-customize__item--confirm-remove">
        <div className="feed-customize__item__confirm-remove-wrapper">
          <div className="feed-customize__item__confirm-remove-title">
            {I18n.t('commons.addFollowToFeed.remove.areYouSure')}
          </div>
          <button
            onClick={this.delete}
            className="feed-customize__item__confirm-remove-button"
            type="button"
          >
            {I18n.t('commons.addFollowToFeed.remove.confirm')}
          </button>
          <button
            onClick={this.toggleConfirm}
            className="feed-customize__item__confirm-remove-button"
            type="button"
          >
            {I18n.t('commons.addFollowToFeed.remove.cancel')}
          </button>
          <p className="feed-customize__item__confirm-remove-hint">
            {I18n.t('commons.addFollowToFeed.remove.removeHint')}
          </p>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.confirmRemove) {
      return this.renderConfirm();
    }

    return this.renderFollow();
  }
}
